/**
 * Limits & controls — the customer surface for migration 0043.
 *
 * Four controls, one envelope. Deposit limit and net-loss limit are editable;
 * cool-off and self-exclusion are one-way doors. The screen reads the offered
 * periods, the increase delay and the minimum self-exclusion from `/me`
 * public params, so an operator changing those in the console changes this
 * page without a deploy. What it must never do is offer a shorten or revoke
 * path inside an active window — there is none on the API and none here.
 *
 * Every write replaces the React Query cache with the server's envelope: the
 * server decides whether a change is active or pending, and the page shows
 * what the server said, not what the form guessed.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { limitsApi } from '@/api/endpoints';
import { ApiClientError } from '@/api/client';
import { useInvalidateMe, useMe } from '@/hooks/useMe';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { formatCents } from '@/utils/money';
import type { ResponsibleGamblingLimits } from '@/types/api';
import {
  COOL_OFF_DAYS,
  type CoolOffDays,
  type LimitKind,
  type SelfExclusionChoice,
  buildLimitPayload,
  classifyChange,
  describeDelay,
  describePeriod,
  dollarsToCents,
  limitView,
  restrictionView,
  selfExclusionOptions,
  validateLimitInput,
} from './logic';

import { LIMITS_QUERY_KEY } from './queryKey';

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

export function LimitsPage() {
  const { data: me } = useMe();
  const { data: limits, isLoading, isError } = useQuery({
    queryKey: LIMITS_QUERY_KEY,
    queryFn: limitsApi.get,
    staleTime: 15_000,
  });

  const params = me?.params;

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <div>
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-teal">
          Account
        </span>
        <h1 className="font-display font-extrabold text-3xl sm:text-[34px] tracking-[-0.02em] mt-1">
          Limits &amp; controls
        </h1>
        <p className="mt-2 text-sm text-white/55 leading-relaxed">
          Set the most you can deposit or lose over a rolling period, take a break, or close your
          account to betting. Tightening a limit applies straight away; loosening one waits.
        </p>
      </div>

      {isLoading && <p className="text-sm text-white/45">Loading your limits…</p>}
      {isError && <p className="text-sm text-red-400">Could not load your limits. Please try again.</p>}

      {limits && params && (
        <>
          <RestrictionBanner limits={limits} />
          <LimitCard kind="deposit" limits={limits} params={params} />
          <LimitCard kind="loss" limits={limits} params={params} />
          <CoolOffCard limits={limits} />
          <SelfExclusionCard limits={limits} params={params} />
          <Card title="Need to talk to someone?">
            <p className="text-sm text-white/55 leading-relaxed">
              Gambling help is free, confidential and available 24/7 on{' '}
              <a href="tel:1800858858" className="text-brand-teal hover:underline">
                1800 858 858
              </a>{' '}
              or at{' '}
              <a
                href="https://www.gamblinghelponline.org.au"
                target="_blank"
                rel="noreferrer"
                className="text-brand-teal hover:underline"
              >
                gamblinghelponline.org.au
              </a>
              .
            </p>
          </Card>
        </>
      )}
    </div>
  );
}

type Params = NonNullable<ReturnType<typeof useMe>['data']>['params'];

function RestrictionBanner({ limits }: { limits: ResponsibleGamblingLimits }) {
  const r = restrictionView(limits);
  if (!r) return null;
  return (
    <div className="rounded-2xl border border-brand-gold/30 bg-brand-gold/[0.07] p-4">
      <p className="text-sm font-semibold text-brand-gold">{r.heading}</p>
      <p className="mt-1 text-sm text-white/70 leading-relaxed">{r.detail}</p>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Deposit / loss limit                                                      */
/* ------------------------------------------------------------------------ */

const KIND_COPY: Record<LimitKind, { title: string; what: string }> = {
  deposit: {
    title: 'Deposit limit',
    what: 'The most you can add to your account in the period.',
  },
  loss: {
    title: 'Loss limit',
    what: 'The most you can lose, net of wins, in the period. Bets still open count in full, and so do fees.',
  },
};

function LimitCard({
  kind,
  limits,
  params,
}: {
  kind: LimitKind;
  limits: ResponsibleGamblingLimits;
  params: Params;
}) {
  const qc = useQueryClient();
  const invalidateMe = useInvalidateMe();
  const view = limitView(limits, kind);
  const offered = params.responsibleGamblingLimitPeriodDays;
  const delay = describeDelay(params.responsibleGamblingIncreaseDelayHours);

  const [editing, setEditing] = useState(false);
  const [dollars, setDollars] = useState('');
  const [period, setPeriod] = useState<number | null>(null);
  const [touched, setTouched] = useState(false);
  const [removing, setRemoving] = useState(false);

  const save = useMutation({
    mutationFn: (body: ReturnType<typeof buildLimitPayload>) => limitsApi.put(body),
    onSuccess: (resp) => {
      qc.setQueryData(LIMITS_QUERY_KEY, resp);
      invalidateMe();
      setEditing(false);
      setRemoving(false);
    },
  });

  const open = () => {
    setDollars(view.activeCents !== null ? String(view.activeCents / 100) : '');
    setPeriod(view.activePeriodDays ?? offered[0] ?? null);
    setTouched(false);
    save.reset();
    setEditing(true);
  };

  const fieldError = validateLimitInput(dollars, period, offered);
  const proposed = fieldError === null ? { cents: dollarsToCents(dollars), periodDays: period } : null;
  const timing = proposed
    ? classifyChange({ cents: view.activeCents, periodDays: view.activePeriodDays }, proposed)
    : null;

  const submit = () => {
    setTouched(true);
    if (!proposed || timing === 'none') return;
    save.mutate(buildLimitPayload(kind, limits.version, proposed.cents, proposed.periodDays));
  };

  const remove = () =>
    save.mutate(buildLimitPayload(kind, limits.version, null, null));

  const copy = KIND_COPY[kind];

  return (
    <Card title={copy.title}>
      <p className="text-xs text-white/40 -mt-1 mb-3">{copy.what}</p>

      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="font-display text-2xl font-extrabold tabular-nums">
            {view.activeCents !== null ? formatCents(view.activeCents) : 'No limit'}
          </div>
          {view.activePeriodDays !== null && (
            <div className="text-xs text-white/45">
              every {describePeriod(view.activePeriodDays)}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {view.activeCents !== null && !view.hasPending && (
            <Button variant="ghost" size="sm" onClick={() => { save.reset(); setRemoving(true); }}>
              Remove
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={open}>
            {view.activeCents === null ? 'Set a limit' : 'Change'}
          </Button>
        </div>
      </div>

      {view.hasPending && (
        <div className="mt-3 rounded-xl border border-brand-gold/25 bg-brand-gold/[0.06] px-3 py-2.5 text-sm">
          <span className="font-semibold text-brand-gold">Change pending.</span>{' '}
          <span className="text-white/70">
            {view.pendingCents === null
              ? 'This limit will be removed'
              : `Becomes ${formatCents(view.pendingCents)} every ${describePeriod(view.pendingPeriodDays ?? 0)}`}{' '}
            on {fmtWhen(view.effectiveAt as string)}. Setting a stricter limit before then applies
            immediately and replaces this.
          </span>
        </div>
      )}

      {/* Edit dialog */}
      <Modal
        open={editing}
        title={view.activeCents === null ? `Set your ${copy.title.toLowerCase()}` : `Change your ${copy.title.toLowerCase()}`}
        onClose={() => setEditing(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button size="sm" loading={save.isPending} onClick={submit} disabled={timing === 'none'}>
              {timing === 'delayed' ? `Schedule change` : 'Apply now'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-white/80">Amount (AUD)</span>
            <div className="flex items-center rounded-lg border border-white/20 bg-brand-surface px-3 focus-within:ring-2 focus-within:ring-brand-teal">
              <span className="text-white/40">$</span>
              <input
                inputMode="decimal"
                value={dollars}
                onChange={(e) => setDollars(e.target.value)}
                className="w-full bg-transparent py-2 pl-1 text-white placeholder-white/30 focus:outline-none"
                placeholder="200"
              />
            </div>
          </label>

          <div>
            <span className="text-sm font-medium text-white/80">Rolling period</span>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {offered.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setPeriod(d)}
                  className={`rounded-lg border px-3 py-1.5 text-sm transition ${
                    period === d
                      ? 'border-brand-teal bg-brand-teal/10 text-brand-teal'
                      : 'border-white/15 text-white/70 hover:bg-white/[0.05]'
                  }`}
                >
                  {describePeriod(d)}
                </button>
              ))}
            </div>
          </div>

          {touched && fieldError && <p className="text-xs text-red-400">{fieldError}</p>}

          {timing === 'immediate' && (
            <p className="rounded-xl border border-brand-green/25 bg-brand-green/[0.06] px-3 py-2 text-sm text-white/75">
              This is a stricter limit, so it applies the moment you confirm.
            </p>
          )}
          {timing === 'delayed' && (
            <p className="rounded-xl border border-brand-gold/25 bg-brand-gold/[0.06] px-3 py-2 text-sm text-white/75">
              This loosens your current limit, so it is held for <strong>{delay}</strong> before it
              takes effect. Your current limit keeps applying until then.
            </p>
          )}
          {timing === 'none' && (
            <p className="text-xs text-white/40">That matches your current limit.</p>
          )}

          {save.error != null && <LimitError error={save.error} />}
        </div>
      </Modal>

      {/* Remove dialog */}
      <Modal
        open={removing}
        title={`Remove your ${copy.title.toLowerCase()}`}
        onClose={() => setRemoving(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setRemoving(false)} disabled={save.isPending}>
              Keep it
            </Button>
            <Button variant="danger" size="sm" loading={save.isPending} onClick={remove}>
              Schedule removal
            </Button>
          </>
        }
      >
        <p className="text-sm text-white/75 leading-relaxed">
          Removing a limit is a loosening, so it is held for <strong>{delay}</strong>. Your current
          limit of {view.activeCents !== null ? formatCents(view.activeCents) : ''} every{' '}
          {view.activePeriodDays !== null ? describePeriod(view.activePeriodDays) : ''} keeps applying
          until then, and you can set a stricter one at any time.
        </p>
        {save.error != null && <LimitError error={save.error} />}
      </Modal>
    </Card>
  );
}

/* ------------------------------------------------------------------------ */
/* Cool-off                                                                  */
/* ------------------------------------------------------------------------ */

function CoolOffCard({ limits }: { limits: ResponsibleGamblingLimits }) {
  const qc = useQueryClient();
  const invalidateMe = useInvalidateMe();
  const [choice, setChoice] = useState<CoolOffDays | null>(null);
  const [confirming, setConfirming] = useState(false);

  const start = useMutation({
    mutationFn: (durationDays: CoolOffDays) => limitsApi.coolOff({ durationDays }),
    onSuccess: (resp) => {
      qc.setQueryData(LIMITS_QUERY_KEY, resp);
      invalidateMe();
      setConfirming(false);
      setChoice(null);
    },
  });

  // The server sets restrictionCode from its own clock; reading that rather
  // than comparing against Date.now() keeps render pure and keeps one authority.
  const active = limits.restrictionCode === 'responsible_gambling_cool_off';
  const excluded = limits.selfExcludedAt !== null;

  return (
    <Card title="Take a break">
      <p className="text-xs text-white/40 -mt-1 mb-3">
        Pause betting for a set time. Once started, a break cannot be ended early — you can only
        extend it.
      </p>

      {active && (
        <p className="mb-3 text-sm text-white/75">
          Your break runs until <strong>{fmtWhen(limits.coolOffUntil as string)}</strong>.
        </p>
      )}

      {excluded ? (
        <p className="text-sm text-white/45">Not available while you are self-excluded.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {COOL_OFF_DAYS.map((d) => (
            <Button
              key={d}
              variant="secondary"
              size="sm"
              onClick={() => { start.reset(); setChoice(d); setConfirming(true); }}
            >
              {active ? 'Extend by' : 'Pause for'} {describePeriod(d)}
            </Button>
          ))}
        </div>
      )}

      <Modal
        open={confirming && choice !== null}
        title="Start a break?"
        onClose={() => setConfirming(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirming(false)} disabled={start.isPending}>
              Not now
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={start.isPending}
              onClick={() => choice !== null && start.mutate(choice)}
            >
              Yes, pause betting
            </Button>
          </>
        }
      >
        <p className="text-sm text-white/75 leading-relaxed">
          You will not be able to place bets for{' '}
          <strong>{choice !== null ? describePeriod(choice) : ''}</strong>
          {active ? ' beyond your current break' : ''}. This cannot be undone or shortened once it
          starts. Your balances stay exactly where they are.
        </p>
        {start.error != null && <LimitError error={start.error} />}
      </Modal>
    </Card>
  );
}

/* ------------------------------------------------------------------------ */
/* Self-exclusion                                                            */
/* ------------------------------------------------------------------------ */

function SelfExclusionCard({
  limits,
  params,
}: {
  limits: ResponsibleGamblingLimits;
  params: Params;
}) {
  const qc = useQueryClient();
  const invalidateMe = useInvalidateMe();
  const options = selfExclusionOptions(params.responsibleGamblingMinSelfExclusionDays);
  const [choice, setChoice] = useState<SelfExclusionChoice | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');

  const start = useMutation({
    mutationFn: (durationDays: SelfExclusionChoice) => limitsApi.selfExclude({ durationDays }),
    onSuccess: (resp) => {
      qc.setQueryData(LIMITS_QUERY_KEY, resp);
      invalidateMe();
      setConfirming(false);
      setChoice(null);
      setTyped('');
    },
  });

  const excluded = limits.selfExcludedAt !== null;
  const permanent = excluded && limits.selfExcludedUntil === null;
  const label = (c: SelfExclusionChoice) => (c === 'permanent' ? 'Permanently' : describePeriod(c));

  return (
    <Card title="Self-exclusion">
      <p className="text-xs text-white/40 -mt-1 mb-3">
        Close your account to betting and deposits for at least{' '}
        {describePeriod(params.responsibleGamblingMinSelfExclusionDays)}, or permanently. This
        cannot be shortened or reversed from the app.
      </p>

      {excluded && (
        <p className="mb-3 text-sm text-white/75">
          {permanent
            ? 'You are permanently self-excluded.'
            : <>You are self-excluded until <strong>{fmtWhen(limits.selfExcludedUntil as string)}</strong>.</>}
        </p>
      )}

      {!permanent && (
        <div className="flex flex-wrap gap-2">
          {options.map((c) => (
            <Button
              key={String(c)}
              variant="secondary"
              size="sm"
              onClick={() => { start.reset(); setTyped(''); setChoice(c); setConfirming(true); }}
            >
              {excluded && c !== 'permanent' ? 'Extend by' : 'Exclude'} {label(c).toLowerCase()}
            </Button>
          ))}
        </div>
      )}

      <Modal
        open={confirming && choice !== null}
        title={choice === 'permanent' ? 'Exclude yourself permanently?' : 'Exclude yourself?'}
        onClose={() => setConfirming(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirming(false)} disabled={start.isPending}>
              Not now
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={start.isPending}
              disabled={typed.trim().toUpperCase() !== 'EXCLUDE'}
              onClick={() => choice !== null && start.mutate(choice)}
            >
              Confirm self-exclusion
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-white/75 leading-relaxed">
            Betting and deposits will be closed on your account{' '}
            {choice === 'permanent' ? (
              <strong>permanently</strong>
            ) : (
              <>for <strong>{choice !== null ? describePeriod(choice) : ''}</strong></>
            )}
            . Nobody — not you, not support — can shorten this from the app once it starts. Your
            Savings and Play Balance stay yours and withdrawals still work.
          </p>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-white/55">Type EXCLUDE to confirm</span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              className="rounded-lg border border-white/20 bg-brand-surface px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-teal"
            />
          </label>
          {start.error != null && <LimitError error={start.error} />}
        </div>
      </Modal>
    </Card>
  );
}

/* ------------------------------------------------------------------------ */

const CODE_COPY: Record<string, string> = {
  version_mismatch: 'Your limits changed in another session. Reload to see the latest, then try again.',
  responsible_gambling_self_excluded: 'Your account is self-excluded, so this is not available.',
  responsible_gambling_cool_off: 'Your account is on a break, so this is not available right now.',
};

function LimitError({ error }: { error: unknown }) {
  const api = error instanceof ApiClientError ? error : null;
  const text =
    (api && CODE_COPY[api.error.code]) ??
    (api?.status === 409
      ? 'Your limits changed in another session. Reload and try again.'
      : api?.error.message ?? 'Something went wrong. Nothing was changed.');
  return (
    <div className="rounded-xl border border-red-500/35 bg-red-500/[0.07] p-3">
      <p className="text-sm text-white/75">{text}</p>
      {api && <p className="mt-1 font-mono text-[11px] text-white/35">{api.error.code}</p>}
    </div>
  );
}
